import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { scrollToElement } from "../hooks/useRoute";

interface Course {
  id: string;
  title: string;
  category: "python" | "web" | "testing" | "marketing" | "security" | "electronics";
  categoryLabel: string;
  badge: string;
  duration: string;
  mode: string;
  description: string;
  syllabus: string[];
  audience: string;
}

const coursesData: Course[] = [
  {
    id: "python-fullstack",
    title: "Python Full-Stack Web Development",
    category: "python",
    categoryLabel: "Python & Full-Stack",
    badge: "High Career Growth",
    duration: "12 Weeks",
    mode: "Live Interactive & Hands-on Projects",
    description: "Comprehensive end-to-end Python full-stack engineering track covering Python 3, Django / FastAPI backend, relational databases, HTML5/CSS3/JavaScript frontend, and REST API deployment.",
    syllabus: [
      "Python 3 Fundamentals, Object-Oriented Programming & Data Structures",
      "Backend Web Frameworks: Django & FastAPI Microservice Architecture",
      "Database Design & ORM: PostgreSQL, SQLite & Data Modeling",
      "Frontend Essentials: HTML5, CSS3, Modern JavaScript (ES6+) & Responsive Layouts",
      "RESTful API Integration, JWT Authentication & Cloud Deployment"
    ],
    audience: "Aspiring software engineers, backend developers, and graduates seeking Python full-stack proficiency.",
  },
  {
    id: "mern-stack",
    title: "MERN Stack Web Development",
    category: "web",
    categoryLabel: "Web & Full-Stack",
    badge: "Most Popular",
    duration: "12 Weeks",
    mode: "Code-Along & Live Projects",
    description: "Master modern full-stack web development using the MERN stack: MongoDB, Express.js, React 18, and Node.js with TypeScript integration.",
    syllabus: [
      "Modern Frontend: React 18, Hooks, State Management & Tailwind CSS",
      "Backend API Server: Node.js & Express.js Microservice Architecture",
      "NoSQL Database: MongoDB & Mongoose Schema Design & Aggregations",
      "Authentication & Security: JWT Tokens, Hashing & Role-Based Access",
      "Full Application Deployment to Managed Cloud Infrastructure"
    ],
    audience: "Web developers, students, and engineers building production React & Node applications.",
  },
  {
    id: "mean-stack",
    title: "MEAN Stack Web Development",
    category: "web",
    categoryLabel: "Web & Full-Stack",
    badge: "Enterprise Ready",
    duration: "12 Weeks",
    mode: "Live Project Track",
    description: "Enterprise full-stack engineering course covering Angular framework, Node.js, Express.js, and MongoDB for structured, scalable web applications.",
    syllabus: [
      "Angular Core: Architecture, Components, Directives, Services & RxJS Observables",
      "TypeScript Deep Dive: Interfaces, Generics, Strong Typing & OOP",
      "Backend Engineering: Node.js, Express.js & RESTful API Architecture",
      "Database Integration: MongoDB Schemas, Queries & Data Validation",
      "Enterprise Deployment, Unit Testing & CI/CD Pipeline Setup"
    ],
    audience: "Developers aiming for enterprise web development and structured Angular/Node architectures.",
  },
  {
    id: "software-testing",
    title: "Software Testing & Quality Assurance",
    category: "testing",
    categoryLabel: "Software Testing",
    badge: "Industry Standard",
    duration: "8 Weeks",
    mode: "Hands-on Testing Labs",
    description: "Complete quality assurance track covering manual testing methodologies, web automation with Selenium & Playwright, API testing, and defect management.",
    syllabus: [
      "Software Testing Fundamentals: SDLC, STLC, Test Plans & Bug Lifecycle",
      "Manual Testing: Functional, Regression, Integration & System Testing",
      "Automated Web Testing: Selenium WebDriver & Playwright with Python/JS",
      "API Testing: Postman, REST Assured & Automated Endpoint Verification",
      "Continuous Testing in CI/CD Pipelines & Defect Tracking Tools"
    ],
    audience: "Aspiring QA engineers, manual testers upgrading to automation, and CS graduates.",
  },
  {
    id: "python-automation",
    title: "Python Programming & Automation",
    category: "python",
    categoryLabel: "Python & Automation",
    badge: "High Demand",
    duration: "6 Weeks",
    mode: "Live Interactive & Hands-on Labs",
    description: "Master Python programming from fundamentals to automated workflow scripts, web scraping, and system utilities.",
    syllabus: [
      "Python Core Syntax, Object-Oriented Programming & Data Structures",
      "File Handling, System Automation & Command-line Tools",
      "Web Scraping & Data Extraction (BeautifulSoup, Selenium)",
      "REST API Consumption & Automated Report Generation",
      "Automated Testing & Real-world Scripting Capstone Project"
    ],
    audience: "Beginners, IT professionals, software testers, and engineers seeking automation skills.",
  },
  {
    id: "python-data-science",
    title: "Data Science & Machine Learning with Python",
    category: "python",
    categoryLabel: "Python & Data",
    badge: "Analytics Track",
    duration: "10 Weeks",
    mode: "Project-Based Learning",
    description: "Comprehensive data science track covering numerical computing, exploratory data analysis, machine learning algorithms, and model deployment.",
    syllabus: [
      "Numerical Computing with NumPy & Data Manipulation with Pandas",
      "Exploratory Data Analysis (EDA) & Data Visualization (Matplotlib, Seaborn)",
      "Supervised & Unsupervised Machine Learning with Scikit-learn",
      "Feature Engineering, Model Optimization & Evaluation Metrics",
      "Deploying ML Models via Web APIs & Real-world Industry Datasets"
    ],
    audience: "Developers, analysts, and STEM graduates aiming for data science & ML roles.",
  },
  {
    id: "react-nextjs",
    title: "Modern React & Next.js Development",
    category: "web",
    categoryLabel: "React & Frontend",
    badge: "Top Choice",
    duration: "8 Weeks",
    mode: "Code-Along & Live Projects",
    description: "Build high-performance, production-ready web applications using React 18, Next.js App Router, Tailwind CSS, and TypeScript.",
    syllabus: [
      "Modern JavaScript (ES6+), Async/Await & Component Architecture",
      "React Core: Hooks (useState, useEffect, useMemo), Context & Custom Hooks",
      "Next.js App Router, Server Components & Server Side Rendering (SSR)",
      "State Management, API Integration & Form Validation",
      "Production Optimization, SEO Best Practices & Cloud Deployment"
    ],
    audience: "Frontend developers, UI engineers, and web enthusiasts upgrading to modern stacks.",
  },
  {
    id: "digital-marketing",
    title: "Performance Digital Marketing & Growth Strategy",
    category: "marketing",
    categoryLabel: "Digital Marketing",
    badge: "Career Focused",
    duration: "8 Weeks",
    mode: "Campaign-Led Practical Labs",
    description: "Master multi-channel digital marketing, search engine optimization (SEO), performance advertising, conversion rate optimization, and brand growth.",
    syllabus: [
      "Technical & On-Page Search Engine Optimization (SEO)",
      "Pay-Per-Click Advertising (Google Ads, Search, Display & Video)",
      "Social Media Marketing & Meta Ads (Instagram, LinkedIn, Facebook)",
      "Web Analytics (Google Analytics 4), Funnel Tracking & Conversion Optimization",
      "Content Marketing Strategy, Email Automation & Brand Positioning"
    ],
    audience: "Entrepreneurs, marketing professionals, business owners, and career seekers.",
  },
  {
    id: "cyber-security",
    title: "Cyber Security & Ethical Hacking Fundamentals",
    category: "security",
    categoryLabel: "Cyber Security",
    badge: "Security Standard",
    duration: "8 Weeks",
    mode: "Virtual Security Lab",
    description: "Gain foundational knowledge in cyber security, OWASP Top 10 vulnerability assessment, network defense, and statutory Indian compliance guidelines.",
    syllabus: [
      "Computer Networking Protocols, Firewalls & Traffic Analysis",
      "OWASP Top 10 Web Application Vulnerabilities & Remediation",
      "Ethical Hacking Methodologies & Penetration Testing Basics",
      "Security Audit Logging, Incident Response & CERT-In Directives",
      "Data Protection Standards (DPDP Act 2023) & System Hardening"
    ],
    audience: "System administrators, developers, and security enthusiasts seeking cyber security fundamentals.",
  },
  {
    id: "embedded-iot",
    title: "Embedded Systems & IoT Hardware Engineering",
    category: "electronics",
    categoryLabel: "Electronics & IoT",
    badge: "Engineering Core",
    duration: "10 Weeks",
    mode: "Hardware Lab & Kit Based",
    description: "Hands-on engineering course on microcontroller programming, circuit design, sensor integration, and Internet of Things (IoT) hardware prototyping.",
    syllabus: [
      "C/C++ Programming for Embedded Microcontrollers (ESP32, ARM Cortex)",
      "Electronic Circuit Fundamentals, PCB Layout Design & Hardware Debugging",
      "Sensor Interfacing (I2C, SPI, UART) & Data Acquisition",
      "IoT Wireless Communication Protocols (Wi-Fi, Bluetooth, MQTT)",
      "Building an End-to-End Smart IoT Hardware Prototype"
    ],
    audience: "Electronics graduates, robotics enthusiasts, and hardware engineering students.",
  },
];

export function Courses({ scrollTo }: { scrollTo?: string }) {
  const [activeCategory, setActiveCategory] = useState<string>("all");

  useEffect(() => {
    if (scrollTo) {
      const targetCourse = coursesData.find((c) => c.id === scrollTo);
      if (targetCourse) {
        setActiveCategory("all");
      }
      scrollToElement(scrollTo);
    }
  }, [scrollTo]);

  const filteredCourses = activeCategory === "all"
    ? coursesData
    : coursesData.filter((c) => c.category === activeCategory);

  return (
    <div className="no-print bg-white dark:bg-[#090e17] transition-colors">
      <PageHero
        badge="RADIECE Technology Education"
        title="Engineering & Technology Courses"
        subtitle="Hands-on, Industry-Aligned Training Programs"
        description="Master Python Full-Stack, MERN, MEAN, Software Testing, React, Data Science, Cyber Security, and Embedded IoT Engineering through practical projects."
        maxWidth="max-w-5xl"
      >
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="#courses-grid"
          className="bg-white/10 border border-white/20 rounded-full px-5 py-2 font-medium hover:bg-white/20 transition-colors"
        >
          Browse All Courses
        </motion.a>
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="/contact"
          className="bg-primary hover:bg-sky-400 text-slate-950 rounded-full px-5 py-2 font-bold transition-colors shadow-md"
        >
          Inquire Admissions & Custom Syllabi
        </motion.a>
      </PageHero>

      {/* Category Navigation Filter */}
      <section id="courses-grid" className="no-print py-12 bg-white dark:bg-[#0b131e] border-b border-slate-200 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { id: "all", label: "All Courses" },
              { id: "python", label: "Python & Full-Stack" },
              { id: "web", label: "Web (MERN / MEAN / React)" },
              { id: "testing", label: "Software Testing & QA" },
              { id: "security", label: "Cyber Security" },
              { id: "electronics", label: "Electronics & IoT" },
              { id: "marketing", label: "Digital Marketing" },
            ].map((tab) => {
              const isActive = activeCategory === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  className={`relative px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer ${
                    isActive
                      ? "text-white dark:text-slate-950 font-bold"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeCourseTabPill"
                      className="absolute inset-0 bg-brand dark:bg-primary rounded-full shadow-xs"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Cards Grid */}
      <section className="no-print py-16 sm:py-20 bg-slate-50 dark:bg-[#070b12] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            <AnimatePresence mode="popLayout">
              {filteredCourses.map((course) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  key={course.id}
                  id={course.id}
                  className="card-interactive scroll-mt-28 bg-white dark:bg-[#0e1724] rounded-2xl border border-slate-200/90 dark:border-slate-800 p-7 sm:p-8 flex flex-col justify-between shadow-xs hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        {course.categoryLabel}
                      </span>
                      <span className="text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full bg-primary/10 dark:bg-primary/20 text-brand dark:text-primary border border-primary/20 dark:border-primary/30">
                        {course.badge}
                      </span>
                    </div>

                    <h2 className="text-2xl font-bold text-brand-dark dark:text-slate-100 mb-3">
                      {course.title}
                    </h2>

                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
                      {course.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500 dark:text-slate-400 mb-6 pb-5 border-b border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-brand dark:text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Duration: {course.duration}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-brand dark:text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.032 14.47A4.5 4.5 0 008.212 22h7.576a4.5 4.5 0 003.18-7.53l-4.06-4.061a2.25 2.25 0 01-.658-1.591V3.104" />
                        </svg>
                        <span>Mode: {course.mode}</span>
                      </div>
                    </div>

                    {/* Detailed Syllabus */}
                    <div className="space-y-3 mb-6">
                      <p className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary">
                        Key Syllabus Modules
                      </p>
                      <ul className="space-y-2">
                        {course.syllabus.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            <span className="w-5 h-5 rounded-full bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                              {idx + 1}
                            </span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800 mb-6">
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        <strong className="text-slate-700 dark:text-slate-200">Target Audience:</strong> {course.audience}
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
                    <a
                      href="/contact"
                      className="btn-shine flex-1 text-center py-3 px-4 bg-brand dark:bg-primary text-white dark:text-slate-950 hover:bg-brand-dark dark:hover:bg-sky-400 rounded-lg font-semibold text-xs uppercase tracking-wider min-h-[44px] flex items-center justify-center shadow-xs"
                    >
                      Inquire Course Syllabus
                    </a>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Custom Unlisted Course Inquiry Section */}
      <section className="no-print bg-slate-900 py-12 sm:py-16 text-white border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left sm:flex sm:items-center sm:justify-between gap-8">
          <div className="max-w-3xl mb-6 sm:mb-0">
            <span className="inline-block px-3 py-1 bg-primary/10 border border-primary/20 text-primary text-xs font-bold rounded-full uppercase tracking-wider mb-3">
              Unlisted Subjects & Custom Syllabi
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Looking for a Custom Course or Unlisted Technology Topic?
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed font-light">
              Don't see your specific framework, programming language, or corporate training requirement listed above? We design customized technical syllabi, corporate engineering curricula, and specialized institutional programs tailored to your exact learning or business goals.
            </p>
          </div>
          <div className="shrink-0">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-6 py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-primary/20"
            >
              Enquire Custom Course →
            </a>
          </div>
        </div>
      </section>

      {/* Why Learn With RADIECE */}
      <section className="no-print py-16 sm:py-20 bg-white dark:bg-[#0b131e] transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Engineering Learning Methodology</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Why Learn With RADIECE?
            </h2>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">
              Our training programs focus on building practical engineering capabilities and production confidence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 dark:bg-[#0e1724] rounded-2xl p-7 border border-slate-100 dark:border-slate-800 transition-colors">
              <div className="w-12 h-12 bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                01
              </div>
              <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">Project-Driven Curriculum</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Work directly on production-grade projects, code repositories, and practical problem statements rather than passive lectures.
              </p>
            </div>

            <div className="bg-slate-50 dark:bg-[#0e1724] rounded-2xl p-7 border border-slate-100 dark:border-slate-800 transition-colors">
              <div className="w-12 h-12 bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                02
              </div>
              <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">Practitioner Instructors</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Learn directly from active software engineers, system architects, and technical consultants with industry experience.
              </p>
            </div>

            <div className="bg-slate-50 dark:bg-[#0e1724] rounded-2xl p-7 border border-slate-100 dark:border-slate-800 transition-colors">
              <div className="w-12 h-12 bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                03
              </div>
              <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">Career & Skill Mentorship</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Receive code reviews, technical resume auditing, portfolio building guidance, and direct preparation for engineering roles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Navigation CTA */}
      <section className="no-print bg-slate-900 py-16 sm:py-20 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
            Ready to Enroll or Request Custom Corporate Training?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base sm:text-lg mb-8 leading-relaxed">
            Submit your course inquiry, custom topic request, or institutional batch inquiry directly to our technical desk.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-200 shadow-lg"
          >
            Go to Contact Page & Enroll →
          </a>
        </div>
      </section>
    </div>
  );
}
